import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import authService from '../services/authService';
import { jwtDecode } from 'jwt-decode'; // Ensure you have this library

const AuthContext = createContext(null);
export const useAuth = () => useContext(AuthContext);

const LEGACY_TILL_STORAGE_KEY = 'thisTillId';
const STORE_TILL_STORAGE_KEY = 'thisTillByStore';

const normalizePermissions = (source) => {
  if (!Array.isArray(source)) return [];
  return source.filter((permission) => typeof permission === 'string' && permission.trim().length > 0);
};

export const derivePermissions = (userData, tokenPermissions = []) => {
  const rolePermissions = (userData?.roles || []).flatMap((role) => normalizePermissions(role?.permissions));
  if (rolePermissions.length > 0) {
    return Array.from(new Set(rolePermissions));
  }
  return Array.from(new Set(normalizePermissions(tokenPermissions)));
};

const parseTillMap = (raw) => {
  if (!raw) return {};
  try {
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return {};
    return Object.entries(parsed).reduce((acc, [storeId, tillId]) => {
      if (typeof storeId !== 'string' || !storeId.trim()) return acc;
      if (typeof tillId !== 'string' || !tillId.trim()) return acc;
      acc[storeId] = tillId;
      return acc;
    }, {});
  } catch (err) {
    return {};
  }
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [isLoading, setIsLoading] = useState(true);
  const [tillId, setTillId] = useState(localStorage.getItem(LEGACY_TILL_STORAGE_KEY));
  const [tillByStore, setTillByStore] = useState(() => parseTillMap(localStorage.getItem(STORE_TILL_STORAGE_KEY)));

  useEffect(() => {
    const loadUserFromToken = async () => {
      if (token) {
        try {
          // --- THE FIX FOR PAGE RELOADS ---
          const decoded = jwtDecode(token);
          if (decoded.exp * 1000 < Date.now()) throw new Error("Token expired");

          authService.setAuthToken(token);
          const { data } = await authService.getMe();
          const nextPermissions = derivePermissions(data, decoded.permissions || []);

          // Combine user data from API with effective permissions.
          setUser({ ...data, permissions: nextPermissions });

        } catch (error) {
          setUser(null);
          setToken(null);
          localStorage.removeItem('token');
        }
      }
      setIsLoading(false);
    };
    loadUserFromToken();
  }, [token]);

  const login = async (credentials, options = {}) => {
    const loginResponse = await authService.login(credentials, options);
    if (loginResponse?.mfaRequired) {
      return loginResponse;
    }
    const { token: newToken, user: loggedInUser } = loginResponse;
    if (!newToken || !loggedInUser) {
      throw new Error('Invalid login response.');
    }
    const decoded = jwtDecode(newToken);
    const nextPermissions = derivePermissions(loggedInUser, decoded.permissions || []);
    const userWithPermissions = { ...loggedInUser, permissions: nextPermissions };

    setToken(newToken);
    setUser(userWithPermissions); // Set the combined object in state
    
    return userWithPermissions;
  };

  const verifyMfa = async ({ challengeToken, code }) => {
    const { token: newToken, user: loggedInUser } = await authService.verifyMfa({ challengeToken, code });
    const decoded = jwtDecode(newToken);
    const nextPermissions = derivePermissions(loggedInUser, decoded.permissions || []);
    const userWithPermissions = { ...loggedInUser, permissions: nextPermissions };

    setToken(newToken);
    setUser(userWithPermissions); // Set the combined object in state
    
    return userWithPermissions;
  };

  const logout = () => {
    authService.logout();
    setUser(null);
    setToken(null);
  };

  const setCurrentTill = useCallback((id) => {
    if (id) {
      localStorage.setItem(LEGACY_TILL_STORAGE_KEY, id);
    } else {
      localStorage.removeItem(LEGACY_TILL_STORAGE_KEY);
    }
    setTillId(id);
  }, []);

  const getCurrentTillForStore = useCallback((storeId) => {
    if (storeId) {
      return tillByStore?.[storeId] || null;
    }
    return tillId || null;
  }, [tillByStore, tillId]);

  const setCurrentTillForStore = useCallback((storeId, id) => {
    if (!storeId) {
      setCurrentTill(id);
      return;
    }

    setTillByStore((prev) => {
      const next = { ...(prev || {}) };
      if (id) {
        next[storeId] = id;
      } else {
        delete next[storeId];
      }
      localStorage.setItem(STORE_TILL_STORAGE_KEY, JSON.stringify(next));
      return next;
    });

    // Keep legacy key in sync with the latest explicit till assignment.
    if (id) {
      localStorage.setItem(LEGACY_TILL_STORAGE_KEY, id);
    } else {
      localStorage.removeItem(LEGACY_TILL_STORAGE_KEY);
    }
    setTillId(id || null);
  }, [setCurrentTill]);

  const updateUser = (updatedUser) => {
    setUser(updatedUser);
  };

  const value = {
    user,
    token,
    isAuthenticated: !!user,
    isLoading,
    login,
    verifyMfa,
    logout,
    tillId,
    tillByStore,
    getCurrentTillForStore,
    setCurrentTill,
    setCurrentTillForStore,
    updateUser,
  };
  
  return (
    <AuthContext.Provider value={value}>
      {!isLoading && children}
    </AuthContext.Provider>
  );
};
